import { ReportContext } from "@reporting/rp-types/analytics";
import type { BroadcastMessage } from "@reporting/rp-app-host/src/container/broadcast";
import { CustomAction, CustomParams, ExportType, ExtensionDetailEventData } from "../types";

export const RPEX_PROTOCOL = 1;

/** messages posted by the extension page (iframe) to the embedding host */
export type ClientMessages = {
	/** the page is bootstrapped and waits for 'init' before running the engine */
	ready: { idProject: number };
	/** the engine finished its first run; snapshot of everything the host UI needs */
	load: { actions: CustomAction[]; exportTypes: ExportType[]; parameters: CustomParams; isSavable: boolean };
	/** drill to another extension resource (right click / cell detail) */
	detail: ExtensionDetailEventData;
	/** a query result landed in the page's DataRelay - the host keeps a copy for reloads */
	cache: { hash: string; value: unknown };
	broadcast: { message: BroadcastMessage };
	error: { message: string };
};

/** messages posted by the embedding host to the extension page */
export type HostMessages = {
	/** reply to 'ready'; also re-sent after every iframe reload (re-parenting) */
	init: { context?: ReportContext; mainResourceContent?: string; cache?: [string, unknown][] };
	context: { context: ReportContext };
	/** editor preview push - re-render without a page reload */
	refresh: { mainResourceContent?: string };
	action: { id: string; data?: unknown };
	export: { type: ExportType };
	broadcast: { message: BroadcastMessage };
};

export type BridgeMessage<TMessages, TType extends keyof TMessages = keyof TMessages> = {
	rpex: typeof RPEX_PROTOCOL;
	type: TType;
	payload: TMessages[TType];
};

export const isBridgeMessage = <TMessages>(data: unknown): data is BridgeMessage<TMessages> => {
	return !!data && typeof data === 'object' && (data as { rpex?: number }).rpex === RPEX_PROTOCOL;
};

/**
 * A broadcast message relayed across the bridge carries the uid of the bridge that
 * injected it, so the relaying side never sends it back to where it came from.
 */
export type RelayedBroadcastMessage = BroadcastMessage & { __rpexSource?: string };
